import { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
    Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "../ui/command";
import { cn } from "@/lib/utils";

/**
 * Durchsuchbare Marken-/Auswahl-Combobox mit Freitext-Override.
 * - `groups`: [{ heading?, items: [{ value, node? }] }]
 * - Nutzer kann tippen und eine vorhandene Marke wählen ODER eine fehlende/falsche
 *   Marke als eigenen Wert übernehmen (Freitext), z. B. wenn eine Marke fehlt.
 * - `clearLabel`: optionaler Reset-Eintrag (z. B. „Alle Marken") → setzt "".
 */
export default function BrandCombobox({
    value,
    onValueChange,
    groups = [],
    placeholder = "Auswählen…",
    searchPlaceholder = "Suchen oder eingeben…",
    ariaLabel,
    testId,
    triggerClassName,
    clearLabel,
    allowCustom = true,
    disabled = false,
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const allValues = groups.flatMap((g) => g.items.map((i) => i.value));
    const term = search.trim();
    const exact = allValues.some((v) => v.toLowerCase() === term.toLowerCase());
    const canCreate = allowCustom && term.length > 0 && !exact;

    const choose = (v) => {
        onValueChange(v);
        setOpen(false);
        setSearch("");
    };

    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(""); }}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        "flex h-12 w-full items-center justify-between gap-2 rounded-sm border border-gray-300 bg-white px-3 text-sm text-left disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#FFCC00]",
                        triggerClassName,
                    )}
                    data-testid={testId}
                    aria-label={ariaLabel || placeholder}
                >
                    <span className={cn("truncate", !value && "text-muted-foreground")}>
                        {value || placeholder}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]"
                align="start"
            >
                <Command>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        value={search}
                        onValueChange={setSearch}
                        data-testid={testId ? `${testId}-input` : undefined}
                    />
                    <CommandList>
                        <CommandEmpty>{canCreate ? null : "Keine Treffer"}</CommandEmpty>
                        {canCreate && (
                            <CommandGroup heading="Eigene Eingabe">
                                <CommandItem
                                    value={`__create__ ${term}`}
                                    onSelect={() => choose(term)}
                                    data-testid={testId ? `${testId}-create` : undefined}
                                >
                                    <Plus className="h-4 w-4 text-[#EA580C]" />
                                    <span>„{term}" übernehmen</span>
                                </CommandItem>
                            </CommandGroup>
                        )}
                        {clearLabel && (
                            <CommandGroup>
                                <CommandItem value="__clear__" onSelect={() => choose("")}>
                                    {clearLabel}
                                    {!value && <Check className="ml-auto h-4 w-4" />}
                                </CommandItem>
                            </CommandGroup>
                        )}
                        {groups.map((g, gi) => (
                            <CommandGroup key={g.heading || `g-${gi}`} heading={g.heading}>
                                {g.items.map((it) => (
                                    <CommandItem
                                        key={`${g.heading || gi}-${it.value}`}
                                        value={it.value}
                                        onSelect={() => choose(it.value)}
                                        data-testid={`brand-opt-${it.value}`}
                                    >
                                        {it.node || it.value}
                                        {value === it.value && <Check className="ml-auto h-4 w-4" />}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
